import React, { useState } from 'react';
import { Box, ClickAwayListener, Tooltip, type TooltipProps } from '@mui/material';
import { useViewport } from '../hooks/useViewport';

type TapTooltipProps = Omit<
  TooltipProps,
  'open' | 'onOpen' | 'disableHoverListener' | 'disableFocusListener' | 'disableTouchListener'
>;

// A Tooltip never opens on a tap, so on a touch pointer this one opens on a tap of its trigger
// and closes on a tap outside; with a mouse it is a plain hover Tooltip (FE-15). The wrapper
// holds the tooltip's portalled content in its React tree, so a tap on that content (a copy
// button, say) is not a tap outside. It is there for both pointers so layout does not change;
// the click-away listener, a document listener per tooltip, is mounted on touch only.
const TapTooltip: React.FC<TapTooltipProps> = ({ children, onClose, ...tooltipProps }) => {
  const { isTouch } = useViewport();
  const [open, setOpen] = useState(false);

  const close = (event: Event | React.SyntheticEvent) => {
    setOpen(false);
    onClose?.(event as React.SyntheticEvent);
  };

  const child = children as React.ReactElement<React.HTMLAttributes<HTMLElement>>;
  const trigger = isTouch
    ? React.cloneElement(child, {
        role: 'button',
        'aria-expanded': open,
        onClick: (event: React.MouseEvent<HTMLElement>) => {
          child.props.onClick?.(event);
          setOpen((wasOpen) => !wasOpen);
        },
      })
    : child;

  const tooltip = (
    <Box sx={{ minWidth: 0 }}>
      <Tooltip
        {...tooltipProps}
        open={isTouch ? open : undefined}
        onClose={close}
        disableHoverListener={isTouch}
        disableFocusListener={isTouch}
        disableTouchListener={isTouch}
      >
        {trigger}
      </Tooltip>
    </Box>
  );

  return isTouch ? <ClickAwayListener onClickAway={close}>{tooltip}</ClickAwayListener> : tooltip;
};

export default TapTooltip;
